import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getStaff, getCardBalances, getAmazonBalances, cardBalanceTransaction, getBalanceTransactions } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Minus, CreditCard, AmazonLogo, ArrowUp, ArrowDown } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function BalancePage() {
  const { user } = useAuth();
  const isAllowed = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'accountant';
  const [staffList, setStaffList] = useState([]);
  const [cardBalances, setCardBalances] = useState([]);
  const [amazonBalances, setAmazonBalances] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    staff_id: '',
    amount: '',
    transaction_type: 'add',
    notes: ''
  });

  const isOwner = user?.role === 'admin' || user?.role === 'owner';
  const isAdmin = isOwner || user?.role === 'assistant';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [cardRes, amazonRes, transRes] = await Promise.all([
        getCardBalances(),
        getAmazonBalances(),
        getBalanceTransactions()
      ]);
      setCardBalances(cardRes.data);
      setAmazonBalances(amazonRes.data);
      setTransactions(transRes.data);
      
      if (isAdmin) {
        const staffRes = await getStaff();
        setStaffList(staffRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAllowed) fetchData();
  }, [fetchData, isAllowed]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.staff_id) {
      toast.error('Please select a staff member');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    try {
      await cardBalanceTransaction({
        staff_id: formData.staff_id,
        amount: parseFloat(formData.amount),
        transaction_type: formData.transaction_type,
        notes: formData.notes || null
      });
      toast.success(`Balance ${formData.transaction_type === 'add' ? 'added' : 'deducted'} successfully`);
      setDialogOpen(false);
      setFormData({ staff_id: '', amount: '', transaction_type: 'add', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update balance');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Combine balances for display
  const combinedBalances = staffList.map(staff => {
    const cardBal = cardBalances.find(c => c.staff_id === staff.id);
    const amazonBal = amazonBalances.find(a => a.staff_id === staff.id);
    return {
      ...staff,
      card_balance: cardBal?.balance || 0,
      amazon_balance: amazonBal?.balance || 0
    };
  });

  const totalCardBalance = cardBalances.reduce((sum, b) => sum + b.balance, 0);
  const totalAmazonBalance = amazonBalances.reduce((sum, b) => sum + b.balance, 0);

  if (!isAdmin) {
    return <div className="p-4 text-red-600">Access denied.</div>;
  }

  if (!isAllowed) return <div className="p-6 text-center text-red-600 font-medium" data-testid="balance-access-denied">Access denied. Only Admin or CA can view Balance Management.</div>;

  return (
    <div className="space-y-6" data-testid="balance-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Balance Management</h1>
        {isOwner && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-balance-btn">
              <Plus size={18} className="mr-2" /> Add/Deduct Balance
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Card Balance Transaction</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="form-label">Staff</Label>
                <SearchableSelect
                  options={staffList.map(s => ({ value: s.id, label: s.name, sublabel: s.email }))}
                  value={formData.staff_id}
                  onValueChange={(v) => setFormData({ ...formData, staff_id: v })}
                  placeholder="Select Staff"
                  searchPlaceholder="Search by name or email..."
                  data-testid="balance-staff-select"
                />
              </div>

              <div>
                <Label className="form-label">Transaction Type</Label>
                <SearchableSelect
                  options={[
                    { value: 'add', label: 'Add Balance (Admin gives money)' },
                    { value: 'deduct', label: 'Deduct Balance (Staff spent)' }
                  ]}
                  value={formData.transaction_type}
                  onValueChange={(v) => setFormData({ ...formData, transaction_type: v })}
                  placeholder="Select Type"
                  data-testid="balance-type-select"
                />
              </div>

              <div>
                <Label className="form-label">Amount (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="form-input"
                  placeholder="Enter amount"
                  required
                  data-testid="balance-amount-input"
                />
              </div>

              <div>
                <Label className="form-label">Notes (Optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="form-input"
                  placeholder="e.g., Opening balance, Closing adjustment"
                  data-testid="balance-notes-input"
                />
              </div>

              <Button type="submit" className="w-full btn-primary" data-testid="balance-submit-btn">
                {formData.transaction_type === 'add' ? 'Add Balance' : 'Deduct Balance'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="stat-card" data-testid="total-card-balance">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard size={24} className="text-[#002FA7]" />
            <span className="stat-label">Total Card Balance (All Staff)</span>
          </div>
          <div className="stat-value text-[#002FA7]">{formatCurrency(totalCardBalance)}</div>
        </div>
        <div className="stat-card" data-testid="total-amazon-balance">
          <div className="flex items-center gap-2 mb-2">
            <AmazonLogo size={24} className="text-[#FF9900]" />
            <span className="stat-label">Total Amazon Balance (All Staff)</span>
          </div>
          <div className="stat-value text-[#FF9900]">{formatCurrency(totalAmazonBalance)}</div>
        </div>
      </div>

      <Tabs defaultValue="balances" className="w-full">
        <TabsList>
          <TabsTrigger value="balances">Staff Balances</TabsTrigger>
          <TabsTrigger value="transactions">Transaction History</TabsTrigger>
        </TabsList>

        <TabsContent value="balances" className="mt-4">
          <div className="card-flat overflow-x-auto">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : (
              <Table className="table-dense">
                <TableHeader>
                  <TableRow>
                    <TableHead>Staff Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="text-right">Card Balance</TableHead>
                    <TableHead className="text-right">Amazon Balance</TableHead>
                    <TableHead className="text-right">Total Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {combinedBalances.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-gray-500">
                        No staff found. Create staff first.
                      </TableCell>
                    </TableRow>
                  ) : (
                    combinedBalances.map((staff) => (
                      <TableRow key={staff.id} data-testid={`balance-row-${staff.id}`}>
                        <TableCell className="font-medium">{staff.name}</TableCell>
                        <TableCell className="text-gray-500">{staff.email}</TableCell>
                        <TableCell className="numeric">
                          <span className="flex items-center justify-end gap-1 text-[#002FA7]">
                            <CreditCard size={16} />
                            {formatCurrency(staff.card_balance)}
                          </span>
                        </TableCell>
                        <TableCell className="numeric">
                          <span className="flex items-center justify-end gap-1 text-[#FF9900]">
                            <AmazonLogo size={16} />
                            {formatCurrency(staff.amazon_balance)}
                          </span>
                        </TableCell>
                        <TableCell className="numeric font-semibold">
                          {formatCurrency(staff.card_balance + staff.amazon_balance)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="transactions" className="mt-4">
          <div className="card-flat overflow-x-auto">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : (
              <Table className="table-dense">
                <TableHeader>
                  <TableRow>
                    <TableHead>Staff</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Balance After</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-gray-500">
                        No transactions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactions.map((t) => (
                      <TableRow key={t.id} data-testid={`transaction-row-${t.id}`}>
                        <TableCell className="font-medium">{t.staff_name}</TableCell>
                        <TableCell>
                          {t.transaction_type === 'add' ? (
                            <span className="flex items-center gap-1 text-green-600">
                              <ArrowUp size={16} /> Added
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-600">
                              <ArrowDown size={16} /> Deducted
                            </span>
                          )}
                        </TableCell>
                        <TableCell className={`numeric ${t.transaction_type === 'add' ? 'text-green-600' : 'text-red-600'}`}>
                          {t.transaction_type === 'add' ? '+' : '-'}{formatCurrency(t.amount)}
                        </TableCell>
                        <TableCell className="numeric font-semibold">{formatCurrency(t.balance_after)}</TableCell>
                        <TableCell className="text-gray-500">{t.notes || '-'}</TableCell>
                        <TableCell>{formatDateTime(t.created_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <div className="card-flat">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">How to use Balance Management</h3>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• <strong>Add Balance:</strong> When admin gives money to staff's card for bookings</li>
          <li>• <strong>Deduct Balance:</strong> When staff spends from card (manual adjustment)</li>
          <li>• <strong>Amazon Balance:</strong> Automatically updated when Amazon refunds are processed</li>
          <li>• Track daily opening/closing balance via Transaction History</li>
        </ul>
      </div>
    </div>
  );
}
